import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { visibleCells } from "@/lib/geometry";
import { coupledJoints } from "@/lib/joints";

let userDetails = [];
export const setUserDetails = (list) => { userDetails = list; };
export const getUserDetails = () => userDetails;

const count = (ops, pred) => ops.filter(pred).length;

function groupsOf(item) {
  const ops = visibleCells(item).map(({ r, c }) => item.cells[r][c]);
  const g = [];
  const sl = count(ops, (o) => o === "sliding_left" || o === "sliding_right");
  if (sl) g.push({ typ: item.type === "door" ? ["sliding_door", "sliding_window", "slim_sliding_window"] : ["sliding_window", "slim_sliding_window", "sliding_door"], shutters: sl });
  const cs = count(ops, (o) => o.startsWith("casement") || o.startsWith("tilt"));
  if (cs) g.push({ typ: ["casement_window", "tilt_turn_window"], shutters: cs });
  if (ops.some((o) => o === "awning" || o === "hopper")) g.push({ typ: ["top_hung_window"] });
  const dr = count(ops, (o) => o.startsWith("door"));
  if (dr) g.push({ typ: dr > 1 ? ["hinged_door_double", "hinged_door_single"] : ["hinged_door_single", "hinged_door_double"], shutters: dr });
  if (ops.some((o) => o.startsWith("sliding_up") || o.startsWith("sliding_down"))) g.push({ typ: ["vertical_sliding_window", "sliding_window"] });
  if (ops.some((o) => o.startsWith("pivot"))) g.push({ typ: ["pivot_window", "casement_window"] });
  if (ops.includes("louvre")) g.push({ typ: ["louvre_window", "fixed_window"] });
  if (ops.some((o) => o.startsWith("folding"))) g.push({ typ: ["folding_door", "hinged_door_double"] });
  if (ops.includes("fixed")) g.push({ typ: ["fixed_window", "storefront"] });
  return g;
}

export function autoDetails(item) {
  const sys = (item.profile_system || "").toLowerCase();
  const jointIds = coupledJoints(item).map((j) => (userDetails.find((d) => d.joint === j.type) || userDetails.find((d) => d.joint === "coupling"))?.detail_id);
  const ids = groupsOf(item).map((g) => {
    let best = null, bs = -1e9;
    userDetails.filter((d) => !d.joint).forEach((d) => {
      const ti = g.typ.indexOf(d.typology);
      if (ti < 0) return;
      let s = 10 - ti * 2;
      if (g.shutters) s -= Math.abs((d.shutters || 1) - g.shutters) * 2;
      const ser = (d.series || "").toLowerCase().replace(/^ykk ap\s*/, "");
      if (ser && sys.includes(ser)) s += 3;
      if (s > bs) { bs = s; best = d; }
    });
    return best?.detail_id;
  });
  return [...new Set([...ids, ...jointIds].filter(Boolean))];
}

export const selectedDetailIds = (item) => item.section_details ?? autoDetails(item);
export const detailsFor = (item) => selectedDetailIds(item).map((id) => userDetails.find((d) => d.detail_id === id)).filter(Boolean);

const cache = new Map(), pending = new Map();
export const cachedImage = (d) => d && cache.get(d.detail_id);

export function loadDetailImage(d) {
  if (cache.has(d.detail_id)) return Promise.resolve(cache.get(d.detail_id));
  if (!pending.has(d.detail_id)) {
    pending.set(d.detail_id, api.get(`/catalogues/${d.catalogue_id}/details/${d.detail_id}/image`, { responseType: "blob" })
      .then(({ data }) => new Promise((res) => {
        const fr = new FileReader();
        fr.onload = () => { cache.set(d.detail_id, fr.result); res(fr.result); };
        fr.readAsDataURL(data);
      }))
      .finally(() => pending.delete(d.detail_id)));
  }
  return pending.get(d.detail_id);
}

export function useDetailImage(d) {
  const [src, setSrc] = useState(() => cachedImage(d));
  useEffect(() => {
    if (!d) return undefined;
    let on = true;
    loadDetailImage(d).then((s) => on && setSrc(s)).catch(() => {});
    return () => { on = false; };
  }, [d?.detail_id]); // eslint-disable-line react-hooks/exhaustive-deps
  return src;
}

export const chunk = (arr, n) => Array.from({ length: Math.ceil(arr.length / n) }, (_, i) => arr.slice(i * n, i * n + n));
